import styled from "@emotion/styled";
import { Skeleton } from "antd5";
import { Flex, Text } from "styles/utility-components";

import Collapsible from "components/ui/collapsible/Collapsible";
import Toggle from "components/ui/toggle/Toggle";
import { useIntegrationSubscriptions } from "lib/hooks/api/lead_subscriptions/useIntegrationSubscriptions";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { SavedNoticeView } from "lib/types/savedViews";

type NotificationEditorProps = {
  selectedViewIds: string[];
  onViewsChange: (viewIds: string[]) => void;
};

function toggleId(id: string, ids: string[]): string[] {
  return ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id];
}

export default function NotificationEditor({
  selectedViewIds,
  onViewsChange,
}: NotificationEditorProps): JSX.Element {
  const settings = useIntegrationSubscriptions();
  const { data: currentUser } = useCurrentUser();
  const savedViews = settings.savedViews ?? [];

  const groups: { header: string; views: SavedNoticeView[] }[] = [
    { header: "Standard", views: savedViews.filter((view) => view.isStandardView) },
    {
      header: "Created by me",
      views: savedViews.filter((view) => view.owner?.guid === currentUser?.guid),
    },
    {
      header: "Created by others",
      views: savedViews.filter(
        (view) => !view.isStandardView && view.owner?.guid !== currentUser?.guid,
      ),
    },
  ];

  return (
    <Container>
      <Text h2>What notifications you receive</Text>
      <Collapsible title="Views" defaultOpen>
        <Flex column gap={24}>
          {settings.status === "ready"
            ? groups.map((group) => (
                <ViewToggleGroup
                  key={group.header}
                  header={group.header}
                  views={group.views}
                  isSelected={(id) => selectedViewIds.includes(id)}
                  onToggle={(id) => onViewsChange(toggleId(id, selectedViewIds))}
                />
              ))
            : [0, 1, 2].map((row) => (
                <Skeleton
                  key={row}
                  active={settings.status === "loading"}
                  paragraph={{ rows: 2, width: "100%" }}
                />
              ))}
        </Flex>
      </Collapsible>
    </Container>
  );
}

type ViewToggleGroupProps = {
  header: string;
  views: SavedNoticeView[];
  isSelected: (id: string) => boolean;
  onToggle: (id: string) => void;
};

function ViewToggleGroup({
  header,
  views,
  isSelected,
  onToggle,
}: ViewToggleGroupProps): JSX.Element {
  return (
    <Flex column>
      <GroupHeader>{header}</GroupHeader>
      {views.length ? (
        views.map((view) => (
          <ViewRow key={view.id}>
            <Text>
              {view.name}
              <DataType> ({view.dataType})</DataType>
            </Text>
            <Toggle
              aria-label={view.name}
              checked={isSelected(view.id)}
              onCheckedChange={() => onToggle(view.id)}
            />
          </ViewRow>
        ))
      ) : (
        <NoViewsMessage>No views available</NoViewsMessage>
      )}
    </Flex>
  );
}

const Container = styled.div({
  display: "grid",
  gap: 16,
});

const GroupHeader = styled.h4({
  margin: "8px 0",
});

const ViewRow = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 0",
  borderRadius: 4,
  "&:nth-of-type(odd)": {
    backgroundColor: theme.colors.sysBackgroundAlternative,
  },
}));

const DataType = styled.span({
  textTransform: "capitalize",
});

const NoViewsMessage = styled.span(({ theme }) => ({
  fontSize: 12,
  lineHeight: "18px",
  color: theme.colors.sysTextSecondary,
}));
